// src/components/home/CtaSection.js
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaCheck, FaClock } from 'react-icons/fa';
import Section from '../common/Section';
import Button from '../common/Button';
import FormField from '../common/FormField';

const CtaSection = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    program: ''
  });
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    // In a real implementation, this would submit to an API
    console.log('Form submitted:', formData);
    // Reset form (optional)
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      program: ''
    });
    // Show success message (would be implemented with a state variable in production)
    alert('Thank you for your interest! We will contact you soon about the next cohort.');
  };
  
  // Countdown timer to next cohort (example date)
  const nextCohortFeatures = [
    '3-month intensive training',
    'Hands-on practical sessions',
    'Industry field trips',
    'Career development workshops',
    'Networking opportunities'
  ];
  
  return (
    <Section background="white" className="border-t border-gray-200">
      <div className="bg-white rounded-lg shadow-xl overflow-hidden">
        <div className="md:flex">
          {/* Left side: Info about next cohort */}
          <div className="md:w-1/2 bg-primary-dark text-white p-8 md:p-10">
            <h2 className="text-3xl font-bold mb-4">Join Our Next Cohort</h2>
            <p className="text-lg mb-6">
              Applications are now open for Cohort 13. Take the first step toward transforming your career with marketable digital skills.
            </p>
            
            <ul className="space-y-4 mb-8">
              {nextCohortFeatures.map((feature, index) => (
                <motion.li 
                  key={index} 
                  className="flex items-center"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <FaCheck className="h-5 w-5 mr-3 text-secondary" />
                  <span>{feature}</span>
                </motion.li>
              ))}
            </ul>
            
            {/* Countdown timer */}
            <div className="bg-primary-dark bg-opacity-50 p-4 rounded-lg mb-8">
              <div className="flex items-center mb-2">
                <FaClock className="mr-2 text-secondary" />
                <span className="font-semibold">Application Deadline</span>
              </div>
              
              <div className="flex space-x-4">
                <div className="text-center">
                  <div className="bg-primary p-3 rounded-lg">
                    <div className="text-2xl font-bold">15</div>
                    <div className="text-sm">Days</div>
                  </div>
                </div>
                <div className="text-center">
                  <div className="bg-primary p-3 rounded-lg">
                    <div className="text-2xl font-bold">08</div>
                    <div className="text-sm">Hours</div>
                  </div>
                </div>
                <div className="text-center">
                  <div className="bg-primary p-3 rounded-lg">
                    <div className="text-2xl font-bold">23</div>
                    <div className="text-sm">Minutes</div>
                  </div>
                </div>
                <div className="text-center">
                  <div className="bg-primary p-3 rounded-lg">
                    <div className="text-2xl font-bold">45</div>
                    <div className="text-sm">Seconds</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Right side: Application form */}
          <div className="md:w-1/2 p-8 md:p-10">
            <h3 className="text-xl font-bold text-primary-dark mb-4">Register Your Interest</h3>
            <p className="text-gray-600 mb-6">
              Fill out this form to receive information about our upcoming cohort and secure your spot.
            </p>
            
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <FormField
                  label="First Name"
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
                <FormField
                  label="Last Name"
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <FormField
                label="Email Address"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
              
              <FormField
                label="Phone Number"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
              
              <div className="mb-6">
                <label className="block mb-2 text-sm font-medium text-gray-800">
                  Program of Interest <span className="text-red-500">*</span>
                </label>
                <select
                  name="program"
                  value={formData.program}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                >
                  <option value="">Select a program</option>
                  <option value="bim">Building Information Modeling</option>
                  <option value="cad">Computer-Aided Design</option>
                  <option value="survey">Land Survey & Photogrammetry</option>
                  <option value="ict">Basic ICT Skills</option>
                  <option value="programming">Computer Programming</option>
                </select>
              </div>
              
              <Button
                type="submit"
                variant="secondary"
                size="lg"
                fullWidth
              >
                Submit Application
              </Button>
            </form>
          </div>
        </div>
      </div>
    </Section>
  );
};

export default CtaSection;